import { createContext, useState, useEffect, useContext, type PropsWithChildren, Dispatch, SetStateAction } from 'react';
import { updateActiveTask, Util } from '../../util/Util';
import { Item } from '../../util/Item';
import { contactFromDetails } from '../client/contactBlock';
import { v4 as uuidv4 } from 'uuid';
import { BatchTask } from '../../util/Batch';
import { useGridApiRef } from '@mui/x-data-grid';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { OFFER_KINDS, type OfferKind } from './offerTypes';
import { businessToday } from "../../util/businessClock";

import { addMoney } from "../../util/quantity";
export interface OfferTask<T> extends BatchTask<T> {
	kind: OfferKind;
	agent: number;
	valid_until: string | null;
}

interface ContextProps {
	pendingTasks: OfferTask<Item>[];
	setPendingTasks: Dispatch<SetStateAction<OfferTask<Item>[]>>;
	fetchedTask: any | null;
	setFetchedTask: (task: any) => void;
	addNewTask: () => void;
	activeTask: OfferTask<Item> | undefined;
	items: any[];
	setItems: (items: any[]) => void;
	apiRef: ReturnType<typeof useGridApiRef<GridApiCommunity>>;
}

const OfferContext = createContext<ContextProps | undefined>(undefined);

export const OfferProvider = ({ children }: PropsWithChildren) => {
	const [pendingTasks, setPendingTasks] = useState<OfferTask<Item>[]>([]);
	const [fetchedTask, setFetchedTask] = useState<any>(null);
	const [items, setItems] = useState<any[]>([]);
	const apiRef = useGridApiRef();
	const activeTask = pendingTasks?.find((task) => task.active);

	const addNewTask = () => {
		const newTask: OfferTask<Item> = {
			id: uuidv4(),
			items: [],
			active: true,
			kind: OFFER_KINDS[0],
			date: businessToday(),
			valid_until: null,
			reference: Util.getDocumentNumber(),
			receiver: -1,
			agent: -1,
			description: '',
			contact: null,
			createdAt: new Date(),
			updatedAt: new Date(),
			total: function () {
				return this.items!.reduce((sum, item) => addMoney(sum, item.total), 0);
			},
		};

		setPendingTasks((prevTasks) =>
			prevTasks.map((task) => ({ ...task, active: false })).concat(newTask)
		);
	};

	useEffect(() => {
		if (!fetchedTask) return;
		const itemRecords = (fetchedTask.records || []).map((record: any) =>
			new Item({
				id: record.item_id,
				item_code: record.item_code,
				item: record.item_name,
				category: record.category,
				price: record.amount,
				quantity: Number(record.planned_qty || record.quantity || 1),
				isFractional: record.is_fractional === true || record.IsFractional === true || items.find((c: any) => c.id === record.item_id)?.IsFractional === true,
			})
		);
		const details = fetchedTask.details || {};
		updateActiveTask(setPendingTasks, {
			...activeTask,
			id: details.batch,
			items: itemRecords,
			kind: (OFFER_KINDS as readonly string[]).includes(details.kind) ? details.kind : OFFER_KINDS[0],
			date: details.date,
			valid_until: details.valid_until || null,
			receiver: details.client_id,
			agent: details.agent_id || -1,
			reference: details.reference,
			description: details.description || '',
			contact: contactFromDetails(details),
		});
	}, [fetchedTask]);

	return (
		<OfferContext.Provider
			value={{
				pendingTasks,
				setPendingTasks,
				fetchedTask,
				setFetchedTask,
				addNewTask,
				activeTask,
				items,
				setItems,
				apiRef,
			}}
		>
			{children}
		</OfferContext.Provider>
	);
};

export const useOffer = () => {
	const context = useContext(OfferContext);
	if (!context) {
		throw new Error('useOffer must be used within an OfferProvider');
	}
	return context;
};
